/*global navigator*/
/* eslint-disable global-require, prefer-const, no-param-reassign */

import {action, computed, observable} from 'mobx';
import {suspectInfiniteLoop} from 'spa/debug';

import {async} from 'spa/server-wait';

const isServer = typeof window === 'undefined';
let loadContentFile;

/*
 * Whether we are in the webpack build context or not so we can load the content in the correct context
 */
if (__WEBPACK__) {
  const ctx = require.context('../../../../../content', false, /\.json$/);
  loadContentFile = content =>
    new Promise(resolve =>
      require.ensure([], () => {
        resolve(ctx(`./${content}.json`));
      }));
} else {
  const {readdirSync} = require('fs');
  const list = readdirSync(`${__dirname}/../../../../../content`);
  loadContentFile = content =>
    Promise.resolve(require(`../../../../../content/${content}.json`));
}

class Content {
  @observable content;

  _pendingContent = null;
  @async
  @action('loadContent - content context')
  @suspectInfiniteLoop
  loadContent(env) {
    // Prevent double loading
    if (this._pendingContent === env) return null;

    this._pendingContent = env;
    return loadContentFile(env)
      .then(action('loaded content', (response) => {
        this.content = response;
        this._pendingContent = null;
      }));
  }

  // Return the full content file
  get FULL_CONTENT() {
    return this.content;
  }

  /*
   * Provides a type fallback to prevent any crash issues with missing content
   */
  getTypeFallback(type) {
    switch (type) {
    case 'string':
      return '';
    case 'number':
      return null;
    case 'object':
      return {};
    default:
      return undefined;
    }
  }

  /*
   * Takes an object base and an array of keys to drill down into the object
   */
  getSubProps(base, keys) {
    return keys.reduce((curVal, nextVal) => {
      if (curVal) {
        return curVal[nextVal];
      }
      return undefined;
    }, base);
  }

  /*
   * Used to pull a specific value from the content file.
   */
  getValue(key, type) {
    if (!key) {
      console.error('No key supplied with content call');
      return '';
    }

    if (!type) {
      console.error('No type supplied with content call');
      return '';
    }

    if (!this.FULL_CONTENT) {
      console.error('No content to load');
      return this.getTypeFallback(type);
    }

    const value = this.FULL_CONTENT[key];

    if (!value) {
      if (__DEV__) {
        console.log(`content not found for ${key} of type:${type} default returned`);
      }
      return this.getTypeFallback(type);
    }

    if (__DEV__) {
      console.log(`content for ${key}:`, value);
    }
    return value;
  }

  /*
   * Used to pull a specific value from the content file. By default it will load the current regions keys
   */
  getLocaleValue(key, locale, type) {
    if (!locale) {
      locale = this.ctx.i18n.contentRegion;
    }

    if (!type) {
      console.error('No type supplied with content call');
      return '';
    }

    if (!this.FULL_CONTENT) {
      console.error('No content to load');
      return this.getTypeFallback(type);
    }

    const value = this.FULL_CONTENT[`${locale}_${key}`];

    if (!value) {
      if (__DEV__) {
        console.log(`content not found for ${key} ${locale} of type:${type} default returned`);
      }
      return this.getTypeFallback(type);
    }

    if (__DEV__) {
      console.log(`content for ${key} ${locale}:`, value);
    }
    return value;
  }

  /*
   * Used to pull a specific nested value from the content file.
   */
  getNestedValue(base, keys = [], locale, type) {
    if (!type) {
      console.error('No type supplied with content call');
    }

    if (!base) {
      console.error('No base supplied with content call');
      return this.getTypeFallback(type);
    }

    if (!this.FULL_CONTENT) {
      console.error('No content to load');
      return this.getTypeFallback(type);
    }

    const keyBase = this.FULL_CONTENT[base];

    if (!keyBase) {
      if (__DEV__) {
        console.log(`content not found for ${base} of type:${type} - default returned`);
      }
      return this.getTypeFallback(type);
    }

    if (keys.length) {
      // get sub properties
      const val = this.getSubProps(keyBase, keys);
      console.log(`content for ${base} ${keys}:`, val);
      return val || this.getTypeFallback(type);
    }

    return keyBase;
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });
  }

  /*
   * Trigger our content loading
   */
  setup() {
    // I know! in a browser!!?? Check the webpack file
    const {NODE_ENV} = process.env;

    const env = isServer ? NODE_ENV : window.ENV;
    this.loadContent(env);
  }
}

export default Content;



// WEBPACK FOOTER //
// ./spa/state/cms-content/index.jsx